/**
 * Preferences Tab Component
 * Displays Asset's payment methods and Ghost preferences
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import { EmptyState, LoadingSpinner } from '../../../../../components/common';
import { UserService } from '../../../../../services/user/UserService';
import type { UserDetail, PaymentMethod } from '../../../../../types/user';

interface PreferencesTabProps {
  assetDetail: UserDetail;
}

const getPaymentMethodTypeLabel = (type: string): string => {
  switch (type) {
    case 'credit_card':
    case 'Card':
      return 'Credit Card';
    case 'debit_card':
      return 'Debit Card';
    case 'bank_account':
    case 'Ach':
      return 'Bank Account';
    default:
      return type;
  }
};

export const PreferencesTab: React.FC<PreferencesTabProps> = ({ assetDetail }) => {
  const theme = useTheme();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setIsLoading(true);
      // Only fetch payment methods for Asset users
      if (assetDetail.userType === 'asset') {
        // First, check if payment methods are already in assetDetail
        if (assetDetail.paymentMethods && assetDetail.paymentMethods.length > 0) {
          console.log('Using payment methods from assetDetail:', assetDetail.paymentMethods);
          setPaymentMethods(assetDetail.paymentMethods);
          setIsLoading(false);
          return;
        }
        
        // Otherwise, try to fetch from API
        const userId = assetDetail.id || assetDetail.userId;
        console.log('Fetching payment methods for user:', userId);
        
        if (userId) {
          const methods = await UserService.getUserPaymentMethods(userId);
          console.log('Fetched payment methods from API:', methods);
          setPaymentMethods(methods);
        } else {
          console.warn('No user ID available for fetching payment methods');
          setPaymentMethods([]);
        }
      } else {
        console.log('User is not an Asset, skipping payment methods fetch');
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setIsLoading(false);
    }
  }, [assetDetail.id, assetDetail.userId, assetDetail.userType, assetDetail.paymentMethods]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  return (
    <Box>
      {/* Payment Methods */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(2) }}>
          <CreditCardIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '1rem',
            }}
          >
            Payment Methods
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LoadingSpinner />
          </Box>
        ) : paymentMethods.length === 0 ? (
          <EmptyState
            title="No Payment Methods"
            message="This Asset has not configured any payment methods."
          />
        ) : (
          <Grid container spacing={2}>
            {paymentMethods.map((method) => (
              <Grid item xs={12} sm={6} md={4} key={method.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(1) }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: theme.typography.subtitle2.fontWeight,
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.body2.fontSize,
                        }}
                      >
                        {getPaymentMethodTypeLabel(method.type)}
                      </Typography>
                      {method.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                    {method.last4 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body2.fontSize,
                          marginBottom: theme.spacing(0.5),
                        }}
                      >
                        •••• {method.last4}
                      </Typography>
                    )}
                    {method.brand && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          textTransform: 'capitalize',
                        }}
                      >
                        {method.brand}
                      </Typography>
                    )}
                    {method.holderName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          display: 'block',
                          marginTop: theme.spacing(0.5),
                        }}
                      >
                        {method.holderName}
                      </Typography>
                    )}
                    {method.expiryMonth && method.expiryYear && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          display: 'block',
                          marginTop: theme.spacing(0.5),
                        }}
                      >
                        Expires: {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

    </Box>
  );
};

